import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Copy, FileSpreadsheet, UserPlus, X } from "lucide-react";
import BaseSelect from "../../Base/BaseSelect";
import BaseButton from "../../Base/BaseButton";
import BulkPassengerModal, { BulkPassengerCreated } from "../../Passengers/BulkPassengerModal";
import { BULK_PASSENGER_MAX, BulkPassengerRow } from "../../Passengers/bulkPassengerValidation";
import useServicesService from "../../../services/useServicesService";
import useCompaniesService from "../../../services/useCompaniesService";
import useAdicionalCatalogService from "../../../services/useAdicionalCatalogService";
import showToast from "../../../utils/toast";
import type { ServiceResponse } from "../../../hooks/useFetch";
import {
  BlockDriverConfig,
  ParsedElAbraExcel,
  ServiceDraftPlan,
  buildDefaultBlockDriverConfigs,
  buildServiceDraftPlans,
  buildServicePlanLabel,
  defaultDriversForPassengers,
  draftPlanToServicePayload,
  anyPlanNeedsCostCenter,
  applyPassengerIdToPlanillaDuplicates,
  collectUnmatchedPassengerSlots,
  dedupeUnmatchedSlotsForBulk,
  planDateLabel,
  planDirectionLabel,
  planillaPassengerIdentityKey,
  planillaPassengerToBulkRow,
  resolveCostCenterOverride,
  shouldResolveAsCatalogLocation,
  inferServiceType,
  minDriversForPassengers,
  movePassengerBetweenVehicles,
  MAX_PASSENGERS_PER_SERVICE,
  plansAreReady,
  remapPassengerOverridesAfterRedistribution,
  resolveBlockPassengerGroups,
  resolveDraftPlans,
  serviceTypeShortLabel,
} from "../../../utils/elAbraExcelImportHelper";
import "./styles/ElAbraExcelImportModal.css";

type Step = "upload" | "review" | "missing" | "creating";

/** Paleta por servicio (bloque): todos sus móviles comparten el mismo acento. */
const SERVICE_TONE_COUNT = 8;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const ElAbraExcelImportModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const { parseElAbraExcel, store, updateAdicional, step2, step3, fetching } = useServicesService();
  const { list: listCompanies } = useCompaniesService();
  const { listManagements, listSuperintendencies, listAreas } = useAdicionalCatalogService();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const paxMoveFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [step, setStep] = useState<Step>("upload");
  const [parsed, setParsed] = useState<ParsedElAbraExcel | null>(null);
  const [blockDrivers, setBlockDrivers] = useState<BlockDriverConfig[]>([]);
  const [plans, setPlans] = useState<ServiceDraftPlan[]>([]);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [missingFields, setMissingFields] = useState<ReturnType<typeof resolveDraftPlans>["missing"]>([]);
  /** Identidad `block|nombre` del pasajero recién movido (animación). */
  const [paxMoveFlash, setPaxMoveFlash] = useState<string | null>(null);
  /** Móviles cuyo cupo acaba de cambiar (animación). */
  const [vehicleCapFlash, setVehicleCapFlash] = useState<Set<string>>(new Set());

  const [companies, setCompanies] = useState<Array<{ value: string; label: string }>>([]);
  const [locations, setLocations] = useState<Array<{ value: string; label: string }>>([]);
  const [passengers, setPassengers] = useState<any[]>([]);
  const [costCenters, setCostCenters] = useState<Array<{ value: string; label: string }>>([]);
  const [managements, setManagements] = useState<Array<{ value: string; label: string }>>([]);
  const [superintendencies, setSuperintendencies] = useState<Array<{ value: string; label: string }>>([]);
  const [areas, setAreas] = useState<Array<{ value: string; label: string }>>([]);
  const [authorizers, setAuthorizers] = useState<Array<{ value: string; label: string }>>([]);
  const [requesters, setRequesters] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedUnmatchedPax, setSelectedUnmatchedPax] = useState<Set<string>>(new Set());
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkModalKey, setBulkModalKey] = useState(0);
  const [bulkInitialRows, setBulkInitialRows] = useState<BulkPassengerRow[]>([]);
  const [bulkPendingKeys, setBulkPendingKeys] = useState<string[]>([]);
  const [showCommonData, setShowCommonData] = useState(true);

  const companyId = overrides.company ? Number(overrides.company) : null;

  const resetState = useCallback(() => {
    setStep("upload");
    setParsed(null);
    setBlockDrivers([]);
    setPlans([]);
    setOverrides({});
    setMissingFields([]);
    setLocations([]);
    setPassengers([]);
    setCostCenters([]);
    setAuthorizers([]);
    setRequesters([]);
    setSelectedUnmatchedPax(new Set());
    setBulkModalOpen(false);
    setBulkInitialRows([]);
    setBulkPendingKeys([]);
    setShowCommonData(true);
    setPaxMoveFlash(null);
    setVehicleCapFlash(new Set());
    if (paxMoveFlashTimer.current) {
      clearTimeout(paxMoveFlashTimer.current);
      paxMoveFlashTimer.current = null;
    }
  }, []);

  const loadCompanyCatalogs = useCallback(
    (cid: number) =>
      new Promise<void>((resolve) => {
        let pending = 2;
        const done = () => {
          pending -= 1;
          if (pending <= 0) resolve();
        };

        step2(cid, {
          onSuccess: (resp: ServiceResponse) => {
            const data = resp?.data ?? {};
            setPassengers(
              (data.passengers ?? []).map((p: any) => ({
                id: p.id,
                name: p.name,
                addresses: p.addresses ?? [],
              }))
            );
            setLocations(
              (data.locations ?? []).map((l: { id: number; name: string }) => ({
                value: String(l.id),
                label: l.name,
              }))
            );
            done();
          },
          onError: () => {
            showToast("Error cargando pasajeros y ubicaciones", "error");
            done();
          },
        });

        step3(cid, {
          onSuccess: (resp: ServiceResponse) => {
            const data = resp?.data ?? {};
            setCostCenters(
              (data.cost_centers ?? []).map((c: { value: number; label: string }) => ({
                value: String(c.value),
                label: c.label,
              }))
            );
            setAuthorizers(
              (data.authorizers ?? []).map((a: { value: number; label: string }) => ({
                value: String(a.value),
                label: a.label,
              }))
            );
            setRequesters(
              (data.requesters ?? []).map((r: { value: number; label: string }) => ({
                value: String(r.value),
                label: r.label,
              }))
            );
            done();
          },
          onError: () => {
            showToast("Error cargando centros de costo", "error");
            done();
          },
        });
      }),
    [step2, step3]
  );

  const refreshPlans = useCallback(
    (nextOverrides = overrides) => {
      if (!parsed) return { resolved: [] as ServiceDraftPlan[], missing: [] };
      const draftPlans = buildServiceDraftPlans(parsed, blockDrivers);
      const { plans: resolved, missing } = resolveDraftPlans(
        draftPlans,
        {
          companyId,
          locations,
          passengers,
          costCenters,
          managements,
          superintendencies,
          areas,
          authorizers,
          requesters,
        },
        nextOverrides
      );
      setPlans(resolved);
      setMissingFields(missing);
      return { resolved, missing };
    },
    [
      parsed,
      blockDrivers,
      overrides,
      companyId,
      locations,
      passengers,
      costCenters,
      managements,
      superintendencies,
      areas,
      authorizers,
      requesters,
    ]
  );

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }
    document.body.style.overflow = "hidden";
    listCompanies({}, {
      onSuccess: (resp: ServiceResponse) => {
        const items = resp?.data?.companies ?? [];
        const opts = items.map((c: { id: number; name: string }) => ({
          value: String(c.id),
          label: c.name,
        }));
        setCompanies(opts);
        const elAbra = opts.find((c: { value: string; label: string }) =>
          c.label.toLowerCase().includes("el abra")
        );
        if (elAbra) {
          setOverrides((prev) => ({ ...prev, company: elAbra.value }));
        }
      },
    });
    listManagements(1, {
      onSuccess: (r) => {
        const list = r?.data?.managements ?? [];
        setManagements(list.map((m: { id: number; name: string }) => ({ value: String(m.id), label: m.name })));
      },
    });
    listSuperintendencies(1, {
      onSuccess: (r) => {
        const list = r?.data?.superintendencies ?? [];
        setSuperintendencies(list.map((m: { id: number; name: string }) => ({ value: String(m.id), label: m.name })));
      },
    });
    listAreas(1, {
      onSuccess: (r) => {
        const list = r?.data?.areas ?? [];
        setAreas(list.map((m: { id: number; name: string }) => ({ value: String(m.id), label: m.name })));
      },
    });
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, listCompanies, listManagements, listSuperintendencies, listAreas, resetState]);

  useEffect(() => {
    if ((step === "missing" || step === "review") && parsed) {
      refreshPlans();
    }
  }, [step, parsed, blockDrivers, locations, passengers, costCenters, authorizers, requesters, refreshPlans]);

  const handleFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      showToast("Use un archivo .xlsx de planilla El Abra", "error");
      return;
    }
    parseElAbraExcel(file, {
      onSuccess: (resp: ServiceResponse) => {
        const data = (resp?.data ?? {}) as ParsedElAbraExcel;
        if (!data.blocks?.length) {
          showToast("No se detectaron solicitudes en el Excel", "error");
          return;
        }
        setParsed(data);
        setBlockDrivers(buildDefaultBlockDriverConfigs(data));
        setStep("review");
        showToast(resp?.message ?? "Planilla leída correctamente", "success");
      },
      onError: (err) => {
        showToast(err?.message ?? "No se pudo leer el Excel", "error");
      },
    });
  };

  const handleDriverCountChange = (blockIndex: number, raw: string) => {
    const block = parsed?.blocks[blockIndex];
    if (!block) return;
    const min = minDriversForPassengers(block.passengers.length);
    const max = block.passengers.length;
    const next = Math.min(max, Math.max(min, Number(raw) || 1));
    setBlockDrivers((prev) =>
      prev.map((row) =>
        row.blockIndex === blockIndex
          ? { blockIndex, driverCount: next }
          : row
      )
    );
  };

  const handleMovePassenger = (
    planKey: string,
    passengerIndex: number,
    toVehicle: number
  ) => {
    if (!parsed) return;
    const plan = plans.find((p) => p.key === planKey);
    if (!plan) return;

    const block = parsed.blocks[plan.blockIndex];
    if (!block) return;

    const config = blockDrivers.find((row) => row.blockIndex === plan.blockIndex);
    const driverCount =
      config?.driverCount ?? defaultDriversForPassengers(block.passengers?.length || 0);
    const currentGroups = resolveBlockPassengerGroups(
      block.passengers || [],
      driverCount,
      config?.groups
    );

    const fromVehicle = plan.vehicleIndex;
    if (toVehicle === fromVehicle) return;

    // Solo entre móviles ya definidos (sin crear nuevos).
    if (toVehicle < 0 || toVehicle >= currentGroups.length) {
      showToast("Ese móvil no existe en este servicio", "info");
      return;
    }

    if ((currentGroups[toVehicle]?.length ?? 0) >= MAX_PASSENGERS_PER_SERVICE) {
      showToast(
        `Móvil ${toVehicle + 1} ya tiene el máximo de ${MAX_PASSENGERS_PER_SERVICE} pasajeros`,
        "info"
      );
      return;
    }

    if ((currentGroups[fromVehicle]?.length ?? 0) <= 1) {
      showToast("Deje al menos un pasajero en el móvil de origen", "info");
      return;
    }

    const result = movePassengerBetweenVehicles(
      currentGroups,
      fromVehicle,
      passengerIndex,
      toVehicle
    );
    if (result.error) {
      showToast(result.error, "error");
      return;
    }

    // Mantener la cantidad de móviles definida en la planificación.
    if (result.groups.length !== currentGroups.length) {
      showToast("No se puede vaciar un móvil; solo redistribuir pasajeros", "info");
      return;
    }

    const nextDrivers = blockDrivers.map((row) =>
      row.blockIndex === plan.blockIndex
        ? {
            blockIndex: plan.blockIndex,
            driverCount: result.groups.length,
            groups: result.groups,
          }
        : row
    );

    const draftPlans = buildServiceDraftPlans(parsed, nextDrivers);
    const remappedOverrides = remapPassengerOverridesAfterRedistribution(
      overrides,
      plans,
      draftPlans
    );

    setSelectedUnmatchedPax((prev) => {
      const selectedIdentities = new Set<string>();
      plans.forEach((oldPlan) => {
        oldPlan.passengers.forEach((p, idx) => {
          const key = `${oldPlan.key}:passenger:${idx}`;
          if (!prev.has(key)) return;
          selectedIdentities.add(
            `${oldPlan.blockIndex}|${planillaPassengerIdentityKey(p.parsed.name)}`
          );
        });
      });
      const next = new Set<string>();
      draftPlans.forEach((newPlan) => {
        newPlan.passengers.forEach((p, idx) => {
          const identity = `${newPlan.blockIndex}|${planillaPassengerIdentityKey(p.parsed.name)}`;
          if (selectedIdentities.has(identity)) {
            next.add(`${newPlan.key}:passenger:${idx}`);
          }
        });
      });
      return next;
    });

    const movedName = plan.passengers[passengerIndex]?.parsed.name || "";
    const movedFlashKey = `${plan.blockIndex}|${planillaPassengerIdentityKey(movedName)}`;

    setBlockDrivers(nextDrivers);
    setOverrides(remappedOverrides);

    if (paxMoveFlashTimer.current) clearTimeout(paxMoveFlashTimer.current);
    setPaxMoveFlash(movedFlashKey);
    setVehicleCapFlash(
      new Set([
        `${plan.blockIndex}:${fromVehicle}`,
        `${plan.blockIndex}:${toVehicle}`,
      ])
    );
    paxMoveFlashTimer.current = setTimeout(() => {
      setPaxMoveFlash(null);
      setVehicleCapFlash(new Set());
      paxMoveFlashTimer.current = null;
    }, 700);
  };

  const previewByBlock = useMemo(() => {
    if (!parsed) return [];
    return parsed.blocks.map((block, blockIndex) => {
      const config = blockDrivers.find((row) => row.blockIndex === blockIndex);
      const driverCount = config?.driverCount ?? defaultDriversForPassengers(block.passengers.length);
      const groups = resolveBlockPassengerGroups(
        block.passengers,
        driverCount,
        config?.groups
      );
      return { blockIndex, block, driverCount, groups, serviceCount: groups.length };
    });
  }, [parsed, blockDrivers]);

  const vehiclesInBlock = useCallback(
    (blockIndex: number) => {
      return plans
        .filter((p) => p.blockIndex === blockIndex)
        .sort((a, b) => a.vehicleIndex - b.vehicleIndex)
        .map((p) => ({
          vehicleIndex: p.vehicleIndex,
          count: p.passengers.length,
          full: p.passengers.length >= MAX_PASSENGERS_PER_SERVICE,
        }));
    },
    [plans]
  );

  /** Color estable por servicio (blockIndex), en orden de aparición. */
  const serviceToneByBlock = useMemo(() => {
    const map = new Map<number, number>();
    let next = 0;
    plans.forEach((plan) => {
      if (!map.has(plan.blockIndex)) {
        map.set(plan.blockIndex, next % SERVICE_TONE_COUNT);
        next += 1;
      }
    });
    return map;
  }, [plans]);

  const totalServices = previewByBlock.reduce((acc, row) => acc + row.serviceCount, 0);

  const handleContinueToMissing = async () => {
    if (!overrides.company) {
      showToast("Seleccione la empresa", "error");
      return;
    }
    await loadCompanyCatalogs(Number(overrides.company));
    setStep("missing");
  };

  const handleOverrideChange = (id: string, value: string) => {
    const next = { ...overrides, [id]: value };
    setOverrides(next);
    if (id === "company" && value) {
      void loadCompanyCatalogs(Number(value));
    }
    refreshPlans(next);
  };

  const handleOverridesBatch = (next: Record<string, string>) => {
    setOverrides(next);
    if (next.company && next.company !== overrides.company) {
      void loadCompanyCatalogs(Number(next.company));
    }
    refreshPlans(next);
  };

  const applyCostCenterToAll = (value: string) => {
    if (!value) return;
    const next: Record<string, string> = { ...overrides, cost_center: value };
    plans.forEach((plan) => {
      if (plan.cost_center_code) next[`${plan.key}:cost_center`] = value;
    });
    parsed?.blocks.forEach((_, blockIndex) => {
      next[`block:${blockIndex}:cost_center`] = value;
    });
    handleOverridesBatch(next);
    showToast("Centro de costo aplicado a todos los servicios", "success");
  };

  const optionLabel = (options: Array<{ value: string; label: string }>, value: string) =>
    options.find((o) => o.value === value)?.label || value;

  const readyPlansCount = useMemo(
    () => (companyId ? plans.filter((plan) => plansAreReady([plan], companyId)).length : 0),
    [plans, companyId]
  );

  const unmatchedPassengers = useMemo(
    () => collectUnmatchedPassengerSlots(plans, overrides),
    [plans, overrides]
  );

  const uniqueUnmatchedCount = useMemo(() => {
    const identities = new Set(
      unmatchedPassengers.map((slot) => planillaPassengerIdentityKey(slot.displayName))
    );
    return identities.size;
  }, [unmatchedPassengers]);

  const selectedUnmatchedCount = useMemo(() => {
    const identities = new Set<string>();
    unmatchedPassengers.forEach((slot) => {
      if (selectedUnmatchedPax.has(slot.overrideKey)) {
        identities.add(planillaPassengerIdentityKey(slot.displayName));
      }
    });
    return identities.size;
  }, [unmatchedPassengers, selectedUnmatchedPax]);

  const bulkPrimaryByIdentity = useMemo(() => {
    const map = new Map<string, (typeof unmatchedPassengers)[number]>();
    unmatchedPassengers.forEach((slot) => {
      if (!selectedUnmatchedPax.has(slot.overrideKey)) return;
      const identity = planillaPassengerIdentityKey(slot.displayName);
      if (!map.has(identity)) map.set(identity, slot);
    });
    return map;
  }, [unmatchedPassengers, selectedUnmatchedPax]);

  const companyLabel = useMemo(
    () => companies.find((c) => c.value === overrides.company)?.label ?? "",
    [companies, overrides.company]
  );

  useEffect(() => {
    if (step !== "missing") return;
    setSelectedUnmatchedPax((prev) => {
      const validKeys = new Set(unmatchedPassengers.map((slot) => slot.overrideKey));
      const next = new Set(Array.from(prev).filter((key) => validKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [step, unmatchedPassengers]);

  const toggleUnmatchedSelection = (overrideKey: string, displayName: string) => {
    const identity = planillaPassengerIdentityKey(displayName);
    setSelectedUnmatchedPax((prev) => {
      const next = new Set(prev);
      if (next.has(overrideKey)) {
        next.delete(overrideKey);
        return next;
      }
      unmatchedPassengers.forEach((slot) => {
        if (planillaPassengerIdentityKey(slot.displayName) === identity) {
          next.delete(slot.overrideKey);
        }
      });
      next.add(overrideKey);
      return next;
    });
  };

  const toggleAllUnmatchedSelection = () => {
    if (selectedUnmatchedCount === uniqueUnmatchedCount && uniqueUnmatchedCount > 0) {
      setSelectedUnmatchedPax(new Set());
      return;
    }
    const next = new Set<string>();
    const seen = new Set<string>();
    unmatchedPassengers.forEach((slot) => {
      const identity = planillaPassengerIdentityKey(slot.displayName);
      if (seen.has(identity)) return;
      seen.add(identity);
      next.add(slot.overrideKey);
    });
    setSelectedUnmatchedPax(next);
  };

  const openBulkModalForSelected = () => {
    if (!companyId) {
      showToast("Seleccione la empresa antes de crear pasajeros", "error");
      return;
    }
    const selected = dedupeUnmatchedSlotsForBulk(
      unmatchedPassengers.filter((slot) => selectedUnmatchedPax.has(slot.overrideKey))
    );
    if (!selected.length) {
      showToast("Seleccione al menos un pasajero sin coincidencia en el sistema", "info");
      return;
    }
    if (selected.length > BULK_PASSENGER_MAX) {
      showToast(`Puede crear hasta ${BULK_PASSENGER_MAX} pasajeros por carga masiva`, "error");
      return;
    }
    setBulkPendingKeys(selected.map((slot) => slot.overrideKey));
    setBulkInitialRows(
      selected.map((slot) => planillaPassengerToBulkRow(slot.plan, slot.passenger))
    );
    setBulkModalKey((prev) => prev + 1);
    setBulkModalOpen(true);
  };

  const handleBulkPassengersCreated = useCallback(
    (created: BulkPassengerCreated[]) => {
      if (!created.length) return;

      const nextOverrides = { ...overrides };
      const catalogById = new Map(passengers.map((p) => [p.id, p]));
      const mergedPassengers = [...passengers];

      created.forEach((p, index) => {
        const overrideKey = bulkPendingKeys[index];
        if (!overrideKey) return;

        let displayName = "";
        plans.forEach((plan) => {
          plan.passengers.forEach((passenger, passengerIndex) => {
            if (`${plan.key}:passenger:${passengerIndex}` === overrideKey) {
              displayName = passenger.parsed.name;
            }
          });
        });

        Object.assign(
          nextOverrides,
          applyPassengerIdToPlanillaDuplicates(plans, displayName, String(p.id), nextOverrides)
        );

        const catalogEntry = {
          id: p.id,
          name: p.name,
          first_lastname: p.first_lastname,
          last_name: p.first_lastname,
          addresses: p.addresses,
        };
        if (!catalogById.has(p.id)) {
          mergedPassengers.push(catalogEntry);
          catalogById.set(p.id, catalogEntry);
        }
      });

      setPassengers(mergedPassengers);
      setOverrides(nextOverrides);
      refreshPlans(nextOverrides);
      setBulkPendingKeys([]);
      setBulkInitialRows([]);
      setSelectedUnmatchedPax(new Set());
    },
    [overrides, passengers, bulkPendingKeys, refreshPlans, plans]
  );

  const handlePassengerOverrideChange = (paxId: string, value: string, displayName: string) => {
    let next = { ...overrides, [paxId]: value };
    if (value) {
      next = applyPassengerIdToPlanillaDuplicates(plans, displayName, value, next);
      const identity = planillaPassengerIdentityKey(displayName);
      setSelectedUnmatchedPax((prev) => {
        const cleared = new Set(prev);
        unmatchedPassengers.forEach((slot) => {
          if (planillaPassengerIdentityKey(slot.displayName) === identity) {
            cleared.delete(slot.overrideKey);
          }
        });
        return cleared;
      });
    }
    if (value) {
      setOverrides(next);
      refreshPlans(next);
      return;
    }
    handleOverrideChange(paxId, value);
  };

  const globalMissing = useMemo(
    () => ({
      company: !overrides.company,
      authorizer: !overrides.authorizer,
      requester: !overrides.requester,
      management: missingFields.some((f) => f.id === "management" && !overrides.management),
      superintendency: missingFields.some((f) => f.id === "superintendency" && !overrides.superintendency),
      area: missingFields.some((f) => f.id === "area" && !overrides.area),
    }),
    [missingFields, overrides]
  );

  const handleCreate = async () => {
    if (!companyId) {
      showToast("Seleccione la empresa", "error");
      return;
    }
    const { resolved } = refreshPlans();
    if (!plansAreReady(resolved, companyId)) {
      showToast("Complete los campos faltantes antes de crear los servicios", "error");
      return;
    }

    setStep("creating");
    let created = 0;
    try {
      for (const plan of resolved) {
        const payload = draftPlanToServicePayload(plan, companyId);
        const resp: any = await store(payload);
        const serviceId = resp?.data?.id ?? resp?.data?.service?.id;
        if (!serviceId) {
          throw new Error(resp?.message ?? "No se pudo crear un servicio");
        }
        if (plan.managementId || plan.superintendencyId || plan.areaId || plan.serviceReasonText) {
          await updateAdicional(Number(serviceId), {
            management: plan.managementId,
            superintendency: plan.superintendencyId,
            area: plan.areaId,
            service_reason_text: plan.serviceReasonText,
          });
        }
        created += 1;
      }
      showToast(`${created} servicio(s) creado(s) desde la planilla`, "success");
      onCreated?.();
      onClose();
    } catch (err: any) {
      showToast(err?.message ?? "Error creando servicios", "error");
      setStep("missing");
    }
  };

  if (!isOpen) return null;

  const ready = plansAreReady(plans, companyId);

  return createPortal(
    <div className="service-request-import-overlay" onClick={onClose}>
      <div
        className={`service-request-import-content${
          step === "missing" || step === "review" ? " service-request-import-content--wide" : ""
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="service-request-import-header">
          <div>
            <h3 className="service-request-import-title">Importar planilla El Abra</h3>
            <p className="service-request-import-subtitle">
              Cada conductor genera un registro de servicio. Desde 5 pasajeros se dividen en 2 móviles por defecto.
              Máximo {MAX_PASSENGERS_PER_SERVICE} pasajeros por móvil; luego puede redistribuirlos entre los móviles definidos.
            </p>
          </div>
          <button type="button" className="service-request-import-close" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </header>

        <div className="service-request-import-body">
          {step === "upload" && (
            <>
              <div
                className="service-request-import-upload"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleFile(file);
                }}
              >
                <FileSpreadsheet size={36} strokeWidth={1.5} />
                <p>Arrastre o seleccione el Excel (.xlsx) de solicitud El Abra</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                  e.target.value = "";
                }}
              />
              {fetching.parseElAbraExcel && (
                <div className="service-request-import-loading">
                  <div className="service-request-import-spinner" />
                  <span>Leyendo planilla…</span>
                </div>
              )}
            </>
          )}

          {step === "review" && parsed && (
            <>
              <div className="service-request-import-summary">
                <strong>{parsed.notes}</strong>
                <ul>
                  <li>{parsed.blocks.length} bloque(s) detectado(s)</li>
                  <li>{totalServices} servicio(s) a crear (1 por conductor)</li>
                </ul>
              </div>

              <div className="service-request-import-field service-request-import-field--readonly">
                <label>Empresa</label>
                <div className="import-company-readonly" title="La importación de planilla es solo para El Abra">
                  {companyLabel || "El Abra"}
                </div>
              </div>

              <div className="import-table-wrap">
                <table className="import-table import-table--review">
                  <thead>
                    <tr>
                      <th className="import-table__col-num">#</th>
                      <th>Fecha</th>
                      <th>Dirección</th>
                      <th>Horario</th>
                      <th className="import-table__col-pax">Pax</th>
                      <th className="import-table__col-drivers">Conductores</th>
                      <th className="import-table__col-count">Servicios</th>
                      <th>Tipo de ruta</th>
                      <th>Distribución por móvil</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewByBlock.map((row) => (
                      <tr key={row.blockIndex}>
                        <td className="import-table__col-num">{row.blockIndex + 1}</td>
                        <td>{row.block.service_date_text || row.block.service_date || "—"}</td>
                        <td>
                          {row.block.direction === "SALIDA"
                            ? "Salida"
                            : row.block.direction === "INGRESO"
                              ? "Ingreso"
                              : `Bloque ${row.blockIndex + 1}`}
                        </td>
                        <td>{row.block.schedule_note || "—"}</td>
                        <td className="import-table__col-pax">{row.block.passengers.length}</td>
                        <td className="import-table__col-drivers">
                          <input
                            id={`drivers-${row.blockIndex}`}
                            type="number"
                            className="import-table__number-input"
                            min={minDriversForPassengers(row.block.passengers.length)}
                            max={row.block.passengers.length}
                            value={row.driverCount}
                            onChange={(e) => handleDriverCountChange(row.blockIndex, e.target.value)}
                            aria-label={`Conductores bloque ${row.blockIndex + 1}`}
                          />
                          <span className="import-table__input-hint">
                            mín. {minDriversForPassengers(row.block.passengers.length)}
                          </span>
                        </td>
                        <td className="import-table__col-count">{row.serviceCount}</td>
                        <td>
                          <span className="import-table__route-type">
                            {serviceTypeShortLabel(inferServiceType(row.block))}
                          </span>
                          {inferServiceType(row.block) === "ORIGEN_DOMICILIOS" && row.block.shared_destination ? (
                            <span className="import-table__route-detail">
                              Destino: {row.block.shared_destination}
                            </span>
                          ) : null}
                          {inferServiceType(row.block) === "DESTINO_DOMICILIOS" && row.block.shared_origin ? (
                            <span className="import-table__route-detail">
                              Origen: {row.block.shared_origin}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          <div className="import-table__split-list">
                            {row.groups.map((group, idx) => (
                              <div key={idx} className="import-table__split-item">
                                {buildServicePlanLabel(
                                  row.block,
                                  group,
                                  inferServiceType(row.block),
                                  { blockIndex: row.blockIndex, vehicleIndex: idx }
                                )}
                              </div>
                            ))}
                          </div>
                          {row.block.passengers.length < 5 && row.driverCount === 1 ? (
                            <span className="import-table__note">
                              &lt;5 pax: puede dividir en más móviles
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {step === "missing" && (
            <div className="import-missing-top">
              <div className="import-missing-progress">
                <div>
                  <strong>Confirmar datos de la planilla</strong>
                  <p style={{ margin: "0.25rem 0 0", fontSize: "0.8125rem", color: "#475569" }}>
                    Toque el número de móvil junto al pasajero para reasignarlo (máx.{" "}
                    {MAX_PASSENGERS_PER_SERVICE} por móvil).
                  </p>
                </div>
                <span className="import-missing-progress-badge">
                  {readyPlansCount} / {plans.length} listos
                </span>
              </div>

              <div className={`import-global-card${showCommonData ? "" : " is-collapsed"}`}>
                <div className="import-global-card__head">
                  <div className="import-global-card__titles">
                    <h4>Datos comunes</h4>
                    {!showCommonData ? (
                      <span className="import-global-card__summary">
                        {companyLabel || "El Abra"}
                        {overrides.authorizer ? " · Autorizador ok" : " · Falta autorizador"}
                        {overrides.requester ? " · Solicitante ok" : " · Falta solicitante"}
                      </span>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className="import-global-card__toggle"
                    onClick={() => setShowCommonData((v) => !v)}
                    aria-expanded={showCommonData}
                  >
                    {showCommonData ? (
                      <>
                        <ChevronUp size={15} />
                        Ocultar
                      </>
                    ) : (
                      <>
                        <ChevronDown size={15} />
                        Mostrar
                      </>
                    )}
                  </button>
                </div>

                {showCommonData ? (
                  <div className="import-global-grid">
                    <div className="import-global-field">
                      <label>Empresa</label>
                      <div
                        className="import-company-readonly"
                        title="La importación de planilla es solo para El Abra"
                      >
                        {companyLabel || "El Abra"}
                      </div>
                      <span className="import-global-field__hint" aria-hidden>
                        &nbsp;
                      </span>
                    </div>
                    <div className="import-global-field">
                      <label>Autorizador</label>
                      <BaseSelect
                        label="Autorizador"
                        hideLabel
                        name="authorizer"
                        placeholder="Seleccionar"
                        options={authorizers}
                        value={overrides.authorizer || ""}
                        onChange={(e) => handleOverrideChange("authorizer", e.target.value)}
                      />
                      <span className="import-global-field__hint" aria-hidden>
                        &nbsp;
                      </span>
                    </div>
                    <div className="import-global-field">
                      <label>Solicitante</label>
                      <BaseSelect
                        label="Solicitante"
                        hideLabel
                        name="requester"
                        placeholder="Seleccionar"
                        options={requesters}
                        value={overrides.requester || ""}
                        onChange={(e) => handleOverrideChange("requester", e.target.value)}
                      />
                      <span className="import-global-field__hint" aria-hidden>
                        &nbsp;
                      </span>
                    </div>

                    {anyPlanNeedsCostCenter(plans) ? (
                      <div className="import-global-field">
                        <label>Centro de costo</label>
                        <BaseSelect
                          label="Centro de costo"
                          hideLabel
                          name="cost_center_global"
                          placeholder="Seleccionar"
                          options={costCenters}
                          value={overrides.cost_center || ""}
                          onChange={(e) => applyCostCenterToAll(e.target.value)}
                        />
                        <span className="import-global-field__hint">Aplica a todos</span>
                      </div>
                    ) : null}

                    {globalMissing.management ? (
                      <div className="import-global-field">
                        <label>Gerencia</label>
                        <BaseSelect
                          label="Gerencia"
                          hideLabel
                          name="management"
                          placeholder="Seleccionar"
                          options={managements}
                          value={overrides.management || ""}
                          onChange={(e) => handleOverrideChange("management", e.target.value)}
                        />
                        <span className="import-global-field__hint">
                          Planilla: «
                          {missingFields.find((f) => f.id === "management")?.extractedText || "—"}»
                        </span>
                      </div>
                    ) : null}

                    {globalMissing.superintendency ? (
                      <div className="import-global-field">
                        <label>Superintendencia</label>
                        <BaseSelect
                          label="Superintendencia"
                          hideLabel
                          name="superintendency"
                          placeholder="Seleccionar"
                          options={superintendencies}
                          value={overrides.superintendency || ""}
                          onChange={(e) => handleOverrideChange("superintendency", e.target.value)}
                        />
                        <span className="import-global-field__hint">
                          Planilla: «
                          {missingFields.find((f) => f.id === "superintendency")?.extractedText || "—"}»
                        </span>
                      </div>
                    ) : null}

                    {globalMissing.area ? (
                      <div className="import-global-field">
                        <label>Área</label>
                        <BaseSelect
                          label="Área"
                          hideLabel
                          name="area"
                          placeholder="Seleccionar"
                          options={areas}
                          value={overrides.area || ""}
                          onChange={(e) => handleOverrideChange("area", e.target.value)}
                        />
                        <span className="import-global-field__hint">
                          Planilla: «{missingFields.find((f) => f.id === "area")?.extractedText || "—"}»
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="import-services-section">
                <div className="import-services-section__head">
                  <h4>Servicios a crear</h4>
                  {unmatchedPassengers.length > 0 ? (
                    <div className="import-bulk-pax-bar">
                      <span className="import-bulk-pax-bar__summary">
                        Busque primero en el catálogo; marque solo los que deba crear. Si un
                        pasajero se repite en varios móviles, solo se elige una vez (máx.{" "}
                        {BULK_PASSENGER_MAX} por carga).
                      </span>
                      <label className="import-bulk-pax-bar__select-all">
                        <input
                          type="checkbox"
                          checked={
                            uniqueUnmatchedCount > 0 &&
                            selectedUnmatchedCount === uniqueUnmatchedCount
                          }
                          onChange={toggleAllUnmatchedSelection}
                        />
                        Seleccionar todos
                      </label>
                      <button
                        type="button"
                        className="import-bulk-pax-bar__btn"
                        onClick={openBulkModalForSelected}
                        disabled={selectedUnmatchedCount === 0 || !companyId}
                      >
                        <UserPlus size={15} />
                        Crear {selectedUnmatchedCount || ""} en carga masiva
                      </button>
                    </div>
                  ) : null}
                </div>
                <div className="import-table-wrap import-table-wrap--services">
                  <table className="import-table import-table--services">
                    <thead>
                      <tr>
                        {unmatchedPassengers.length > 0 ? (
                          <th className="import-table__col-check" aria-label="Seleccionar" />
                        ) : null}
                        <th className="import-table__col-num">#</th>
                        <th className="import-table__col-status">Estado</th>
                        <th>Fecha</th>
                        <th className="import-table__col-time">Hora</th>
                        <th className="import-table__col-dir">Dir.</th>
                        <th className="import-table__col-vehicle">Móvil</th>
                        <th className="import-table__col-route">Ruta</th>
                        <th className="import-table__col-pax-name">Nombre planilla</th>
                        <th className="import-table__col-pax-match">Pasajero catálogo</th>
                        <th className="import-table__col-pax-addr">Domicilio</th>
                        <th className="import-table__col-location">Origen</th>
                        <th className="import-table__col-location">Destino</th>
                        {anyPlanNeedsCostCenter(plans) ? (
                          <th className="import-table__col-cc">Centro de costo</th>
                        ) : null}
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((plan, planIndex) => {
                        const planReady = companyId ? plansAreReady([plan], companyId) : false;
                        const originId = `${plan.key}:origin`;
                        const destId = `${plan.key}:destination`;
                        const ccId = `${plan.key}:cost_center`;
                        const needsOrigin =
                          shouldResolveAsCatalogLocation(plan.originText, plan.service_type, "origin") &&
                          Boolean(plan.originText);
                        const needsDest =
                          shouldResolveAsCatalogLocation(
                            plan.destinationText,
                            plan.service_type,
                            "destination"
                          ) && Boolean(plan.destinationText);
                        const needsCc = Boolean(plan.cost_center_code);
                        const originMissing = missingFields.find((f) => f.id === originId);
                        const destMissing = missingFields.find((f) => f.id === destId);
                        const ccMissing = missingFields.find((f) => f.id === ccId);
                        const ccValue = resolveCostCenterOverride(overrides, plan);
                        const rowSpan = Math.max(plan.passengers.length, 1);
                        const rowClass = planReady
                          ? "import-table__row--ready"
                          : "import-table__row--pending";
                        const paxRows =
                          plan.passengers.length > 0
                            ? plan.passengers
                            : [
                                {
                                  parsed: { name: "—" },
                                  passengerId: null,
                                  addressText: "",
                                  addressId: null,
                                  needsSelection: true,
                                },
                              ];

                        const serviceTone = serviceToneByBlock.get(plan.blockIndex) ?? 0;

                        const serviceCells = (
                          <>
                            <td
                              className={`import-table__col-num import-table__col-num--tone-${serviceTone}`}
                              rowSpan={rowSpan}
                            >
                              {planIndex + 1}
                            </td>
                            <td className="import-table__col-status" rowSpan={rowSpan}>
                              <span
                                className={`import-table__status${
                                  planReady ? " import-table__status--ok" : " import-table__status--pending"
                                }`}
                              >
                                {planReady ? (
                                  <>
                                    <CheckCircle2 size={12} /> Listo
                                  </>
                                ) : (
                                  <>
                                    <AlertCircle size={12} /> Pendiente
                                  </>
                                )}
                              </span>
                            </td>
                            <td rowSpan={rowSpan}>{planDateLabel(plan)}</td>
                            <td className="import-table__col-time" rowSpan={rowSpan}>
                              {plan.service_time || "—"}
                            </td>
                            <td className="import-table__col-dir" rowSpan={rowSpan}>
                              {planDirectionLabel(plan)}
                            </td>
                            <td
                              className={`import-table__col-vehicle import-svc-tone-${serviceTone}`}
                              rowSpan={rowSpan}
                            >
                              <span className="import-table__vehicle-label">
                                Móvil {plan.vehicleIndex + 1}
                              </span>
                              <span
                                className={`import-table__vehicle-cap${
                                  plan.passengers.length >= MAX_PASSENGERS_PER_SERVICE
                                    ? " import-table__vehicle-cap--full"
                                    : ""
                                }${
                                  vehicleCapFlash.has(`${plan.blockIndex}:${plan.vehicleIndex}`)
                                    ? " import-table__vehicle-cap--flash"
                                    : ""
                                }`}
                              >
                                {plan.passengers.length}/{MAX_PASSENGERS_PER_SERVICE}
                              </span>
                            </td>
                            <td className="import-table__col-route" rowSpan={rowSpan}>
                              <span className="import-table__route-type">
                                {serviceTypeShortLabel(plan.service_type)}
                              </span>
                              {plan.originText && needsOrigin ? (
                                <span className="import-table__route-detail">«{plan.originText}»</span>
                              ) : null}
                              {plan.destinationText && needsDest ? (
                                <span className="import-table__route-detail">«{plan.destinationText}»</span>
                              ) : null}
                            </td>
                          </>
                        );

                        const locationCells = (
                          <>
                            <td className="import-table__col-location" rowSpan={rowSpan}>
                              {!needsOrigin ? (
                                <span className="import-table__cell-muted">Domicilios</span>
                              ) : plan.originId || overrides[originId] ? (
                                <span className="import-table__cell-ok-text">
                                  <CheckCircle2 size={12} />
                                  {optionLabel(locations, overrides[originId] || String(plan.originId))}
                                </span>
                              ) : (
                                <div className="import-table__cell-edit">
                                  {originMissing?.extractedText ? (
                                    <span className="import-table__extracted">
                                      «{originMissing.extractedText}»
                                    </span>
                                  ) : null}
                                  <BaseSelect
                                    label=""
                                    name={originId}
                                    placeholder="Origen"
                                    options={
                                      originMissing?.options?.length ? originMissing.options : locations
                                    }
                                    value={overrides[originId] || ""}
                                    onChange={(e) => handleOverrideChange(originId, e.target.value)}
                                  />
                                </div>
                              )}
                            </td>
                            <td className="import-table__col-location" rowSpan={rowSpan}>
                              {!needsDest ? (
                                <span className="import-table__cell-muted">Domicilios</span>
                              ) : plan.destinationId || overrides[destId] ? (
                                <span className="import-table__cell-ok-text">
                                  <CheckCircle2 size={12} />
                                  {optionLabel(locations, overrides[destId] || String(plan.destinationId))}
                                </span>
                              ) : (
                                <div className="import-table__cell-edit">
                                  {destMissing?.extractedText ? (
                                    <span className="import-table__extracted">
                                      «{destMissing.extractedText}»
                                    </span>
                                  ) : null}
                                  <BaseSelect
                                    label=""
                                    name={destId}
                                    placeholder="Destino"
                                    options={
                                      destMissing?.options?.length ? destMissing.options : locations
                                    }
                                    value={overrides[destId] || ""}
                                    onChange={(e) => handleOverrideChange(destId, e.target.value)}
                                  />
                                </div>
                              )}
                            </td>
                            {anyPlanNeedsCostCenter(plans) ? (
                              <td className="import-table__col-cc" rowSpan={rowSpan}>
                                {!needsCc ? (
                                  <span className="import-table__cell-muted">—</span>
                                ) : plan.costCenterId || ccValue ? (
                                  <span className="import-table__cell-ok-text">
                                    <CheckCircle2 size={12} />
                                    {optionLabel(costCenters, ccValue || String(plan.costCenterId))}
                                  </span>
                                ) : (
                                  <div className="import-table__cell-edit">
                                    {ccMissing?.extractedText ? (
                                      <span className="import-table__extracted">
                                        «{ccMissing.extractedText}»
                                      </span>
                                    ) : null}
                                    <BaseSelect
                                      label=""
                                      name={ccId}
                                      placeholder="C. costo"
                                      options={
                                        ccMissing?.options?.length ? ccMissing.options : costCenters
                                      }
                                      value={ccValue}
                                      onChange={(e) => handleOverrideChange(ccId, e.target.value)}
                                    />
                                    {ccValue ? (
                                      <button
                                        type="button"
                                        className="import-apply-all-btn"
                                        onClick={() => applyCostCenterToAll(ccValue)}
                                      >
                                        <Copy size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                                        A todos
                                      </button>
                                    ) : null}
                                  </div>
                                )}
                              </td>
                            ) : null}
                          </>
                        );

                        return paxRows.map((p, paxIdx) => {
                          const paxId = `${plan.key}:passenger:${paxIdx}`;
                          const addrId = `${paxId}:address`;
                          const paxMissing = missingFields.find((f) => f.id === paxId);
                          const addrMissing = missingFields.find((f) => f.id === addrId);
                          const isFirst = paxIdx === 0;
                          const isUnmatched = !p.passengerId && !overrides[paxId];
                          const passengerIdentity = planillaPassengerIdentityKey(p.parsed.name);
                          const primaryBulkSlot = bulkPrimaryByIdentity.get(passengerIdentity);
                          const isBlockedDuplicate =
                            isUnmatched &&
                            Boolean(primaryBulkSlot) &&
                            primaryBulkSlot?.overrideKey !== paxId;
                          const isSelectedForBulk =
                            isUnmatched && selectedUnmatchedPax.has(paxId);
                          const blockVehicles = vehiclesInBlock(plan.blockIndex);
                          const canRedistribute =
                            p.parsed.name !== "—" && blockVehicles.length > 1;
                          const isLastInVehicle = plan.passengers.length <= 1;
                          const paxFlashKey = `${plan.blockIndex}|${passengerIdentity}`;
                          const justArrived = paxMoveFlash === paxFlashKey;

                          return (
                            <tr
                              key={`${plan.key}-p${paxIdx}`}
                              className={`import-svc-tone-${serviceTone} ${rowClass}${
                                isFirst ? "" : " import-table__row--sub"
                              }${
                                isUnmatched && isSelectedForBulk ? " import-table__row--bulk-selected" : ""
                              }${isBlockedDuplicate ? " import-table__row--duplicate-blocked" : ""}${
                                justArrived ? " import-table__row--pax-arrive" : ""
                              }`}
                            >
                              {unmatchedPassengers.length > 0 ? (
                                <td className="import-table__col-check">
                                  {isUnmatched && !isBlockedDuplicate ? (
                                    <input
                                      type="checkbox"
                                      checked={isSelectedForBulk}
                                      onChange={() =>
                                        toggleUnmatchedSelection(paxId, p.parsed.name)
                                      }
                                      aria-label={`Seleccionar ${p.parsed.name} para carga masiva`}
                                    />
                                  ) : isBlockedDuplicate ? (
                                    <span
                                      className="import-table__check-locked"
                                      title={`Incluido en Móvil ${(primaryBulkSlot?.plan.vehicleIndex ?? 0) + 1}`}
                                      aria-hidden
                                    >
                                      ⊘
                                    </span>
                                  ) : null}
                                </td>
                              ) : null}
                              {isFirst ? serviceCells : null}
                              <td className="import-table__col-pax-name" title={p.parsed.name}>
                                {canRedistribute ? (
                                  <div
                                    className={`import-pax-move import-svc-tone-${serviceTone}${
                                      justArrived ? " is-arriving" : ""
                                    }`}
                                  >
                                    <span className="import-pax-move__name">{p.parsed.name}</span>
                                    <div
                                      className="import-pax-move__pills"
                                      role="group"
                                      aria-label={`Móvil de ${p.parsed.name}`}
                                    >
                                      {blockVehicles.map((v) => {
                                        const isCurrent = v.vehicleIndex === plan.vehicleIndex;
                                        const blocked =
                                          (!isCurrent && v.full) ||
                                          (!isCurrent && isLastInVehicle);
                                        let title = `Pasar a Móvil ${v.vehicleIndex + 1}`;
                                        if (isCurrent) title = `En Móvil ${v.vehicleIndex + 1}`;
                                        else if (!isCurrent && v.full) {
                                          title = `Móvil ${v.vehicleIndex + 1} lleno (${MAX_PASSENGERS_PER_SERVICE}/${MAX_PASSENGERS_PER_SERVICE})`;
                                        } else if (isLastInVehicle) {
                                          title = "Deje al menos un pasajero en este móvil";
                                        }
                                        return (
                                          <button
                                            key={v.vehicleIndex}
                                            type="button"
                                            className={`import-pax-move__pill${
                                              isCurrent ? " is-active" : ""
                                            }${v.full && !isCurrent ? " is-full" : ""}`}
                                            disabled={blocked || isCurrent}
                                            title={title}
                                            aria-pressed={isCurrent}
                                            aria-label={title}
                                            onClick={() =>
                                              handleMovePassenger(plan.key, paxIdx, v.vehicleIndex)
                                            }
                                          >
                                            {v.vehicleIndex + 1}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  p.parsed.name
                                )}
                              </td>
                              <td className="import-table__col-pax-match">
                                {paxMissing || !p.passengerId ? (
                                  isBlockedDuplicate ? (
                                    <span
                                      className="import-table__cell-duplicate"
                                      title={`Se creará junto con Móvil ${(primaryBulkSlot?.plan.vehicleIndex ?? 0) + 1}`}
                                    >
                                      Incluido en Móvil {(primaryBulkSlot?.plan.vehicleIndex ?? 0) + 1}
                                    </span>
                                  ) : isSelectedForBulk ? (
                                    <span className="import-table__cell-bulk-pending">
                                      Pendiente de alta masiva
                                    </span>
                                  ) : (
                                    <BaseSelect
                                      label=""
                                      name={paxId}
                                      placeholder="Seleccionar"
                                      options={
                                        paxMissing?.options?.length
                                          ? paxMissing.options
                                          : passengers.map((row) => ({
                                              value: String(row.id),
                                              label: row.name,
                                            }))
                                      }
                                      value={overrides[paxId] || ""}
                                      onChange={(e) =>
                                        handlePassengerOverrideChange(
                                          paxId,
                                          e.target.value,
                                          p.parsed.name
                                        )
                                      }
                                    />
                                  )
                                ) : (
                                  <span className="import-table__cell-ok-text">
                                    <CheckCircle2 size={12} />
                                    {p.parsed.name}
                                  </span>
                                )}
                              </td>
                              <td className="import-table__col-pax-addr">
                                {addrMissing ? (
                                  <span className="import-table__cell-warn">
                                    Sin domicilio
                                    {addrMissing.extractedText ? ` («${addrMissing.extractedText}»)` : ""}
                                  </span>
                                ) : p.addressText && p.passengerId ? (
                                  <span className="import-table__cell-ok-text" title={p.addressText}>
                                    {p.addressText}
                                  </span>
                                ) : (
                                  <span className="import-table__cell-muted">—</span>
                                )}
                              </td>
                              {isFirst ? locationCells : null}
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {plans.length === 0 ? (
                <p className="import-missing-empty">No hay servicios para confirmar.</p>
              ) : null}
            </div>
          )}

          {step === "creating" && (
            <div className="service-request-import-loading">
              <div className="service-request-import-spinner" />
              <span>Creando servicios…</span>
            </div>
          )}
        </div>

        <footer className="service-request-import-footer">
          <BaseButton label="Cancelar" onClick={onClose} disabled={step === "creating"} />
          {step === "review" && (
            <BaseButton
              label="Continuar"
              variant="primary"
              onClick={() => void handleContinueToMissing()}
              disabled={!overrides.company}
            />
          )}
          {step === "missing" && (
            <BaseButton
              label={`Crear ${plans.length} servicio(s)`}
              variant="primary"
              onClick={() => void handleCreate()}
              disabled={!ready || fetching.store}
            />
          )}
        </footer>
      </div>

      <BulkPassengerModal
        key={bulkModalKey}
        isOpen={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        companyId={companyId}
        companyLabel={companyLabel}
        existingPassengers={passengers}
        initialRows={bulkInitialRows}
        stacked
        title="Crear pasajeros desde planilla"
        subtitle="Revise nombre, apellidos y dirección. Al guardar volverá a la importación manteniendo el orden de la planilla."
        onSuccess={handleBulkPassengersCreated}
      />
    </div>,
    document.body
  );
};

export default ElAbraExcelImportModal;
